/**
 * BMD-Export (AP-22 Teil 2, F).
 *
 * Aus den **fertiggestellten** Lohnmonaten entsteht eine Textdatei für den
 * Import in BMD: eine Zeile je Mitarbeiter und Lohnart mit Menge. Bewertet wird
 * in BMD — hier gehen nur Mengen hinaus.
 *
 * Die Seite hat vier Teile: der Export des Monats mit Vorschau, die bisher
 * erzeugten Dateien, die Zuordnung der Größen zu Lohnarten und das Format der
 * Datei. Die Satzbeschreibung von BMD liegt nicht vor; deshalb ist das Format
 * einstellbar und nicht fest verdrahtet.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, FileOutput, Mail, Plus, X } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { apiErrorText } from '@/lib/api-error';
import { bestaetige } from '@/lib/dialoge';
import { downloadFile, extractFilename } from '@/lib/download';
import { exportLaden } from '@/lib/export-request';
import { pageWide } from '@/lib/form-styles';
import { MailExportDialog } from '@/components/mail/MailExportDialog';
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock';
import {
  Abschnitt,
  FeldStatusZeichen,
  FeldZeile,
  SchalterZeile,
  feldKlasse,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  bmdFileRequest,
  createBmdHandover,
  fetchCollectiveAgreements,
  fetchBmdHandovers,
  fetchBmdPreview,
  fetchBmdProfile,
  fetchBmdWageTypes,
  type BmdEinheit,
  type BmdProfil,
  type BmdSpalte,
  type BmdSpaltenArt,
  type BmdUebergabe,
  type BmdUmfang,
  type BmdVorschau,
  type BmdWarnung,
  type BmdZuordnung,
} from '../api/payroll.api';
import type { CollectiveAgreement } from '../types/payroll.types';

const MONATSNAMEN = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const EINHEIT_TEXT: Record<BmdEinheit, string> = { HOURS: 'Stunden', DAYS: 'Tage', COUNT: 'Anzahl', AMOUNT: 'Betrag' };

const SPALTEN_TEXT: Record<BmdSpaltenArt, string> = {
  KLIENT: 'Klientennummer',
  PERSONALNR: 'Personalnummer',
  NAME: 'Name',
  JAHR: 'Jahr',
  MONAT: 'Monat',
  PERIODE: 'Abrechnungsmonat (JJJJMM)',
  LOHNART: 'Lohnart',
  MENGE: 'Menge',
  EINHEIT: 'Einheit',
  NLZ: 'NLZ-Kennzeichen',
  TEXT: 'Text (Bezeichnung der Größe)',
  FEST: 'Fester Wert',
};

const WARNUNG_TEXT: Record<BmdWarnung['code'], string> = {
  NO_PERSONNEL_NUMBER: 'Fertiggestellt, aber ohne Personalnummer — nicht in der Datei',
  NOT_FINAL: 'Noch nicht fertiggestellt — nicht in der Datei',
  UNMAPPED: 'Hat einen Wert, aber keine Lohnart — wird nicht übergeben',
  NO_CLIENT_NUMBER: 'Die Klientennummer ist leer',
};

function zpad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Vorgabe ist der Vormonat — der ist es, der gerade abgeschlossen wird. */
function vormonat(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function verschiebe(monat: string, um: number): string {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(j, m - 1 + um, 1);
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function zeitpunkt(iso: string): string {
  const d = new Date(iso);
  return `${zpad(d.getDate())}.${zpad(d.getMonth() + 1)}.${d.getFullYear()} ${zpad(d.getHours())}:${zpad(d.getMinutes())}`;
}

async function ladeDatei(u: BmdUebergabe) {
  const antwort = await exportLaden(bmdFileRequest(u.id));
  const name = extractFilename(antwort.headers['content-disposition'] as string | undefined, u.filename);
  downloadFile(antwort.data, name, 'text/csv');
}

export default function PayrollBmdPage() {
  const { t } = useTranslation();
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfEinstellen = erfuelltRecht('payroll.bmd.configure', rechte);

  const [params, setParams] = useSearchParams();
  const monat = /^\d{4}-\d{2}$/.test(params.get('month') ?? '') ? params.get('month')! : vormonat();
  const [jahr, mon] = monat.split('-').map(Number);
  const monatName = `${MONATSNAMEN[mon - 1]} ${jahr}`;
  const setzeMonat = (neu: string) => {
    const p = new URLSearchParams(params);
    p.set('month', neu);
    setParams(p, { replace: true });
  };

  const [umfang, setUmfang] = useState<BmdUmfang>('ALL');
  const [vorschau, setVorschau] = useState<BmdVorschau | null>(null);
  const [uebergaben, setUebergaben] = useState<BmdUebergabe[]>([]);
  const [zuordnungen, setZuordnungen] = useState<BmdZuordnung[] | null>(null);
  const [profil, setProfil] = useState<BmdProfil | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [mailFuer, setMailFuer] = useState<BmdUebergabe | null>(null);

  const ladeMonat = useCallback(async () => {
    try {
      const [v, u] = await Promise.all([fetchBmdPreview(monat, umfang), fetchBmdHandovers(monat)]);
      setVorschau(v);
      setUebergaben(u);
    } catch (err) {
      setError(apiErrorText(err, 'Der BMD-Export konnte nicht geladen werden.'));
    }
  }, [monat, umfang]);

  useEffect(() => {
    setError(null);
    void ladeMonat();
  }, [ladeMonat]);

  useEffect(() => {
    setMeldung(null);
  }, [monat]);

  // AP-22 Teil 3: Die Zuordnung gilt „für alle" und lässt sich je Entlohnungsart überschreiben.
  const [arten, setArten] = useState<CollectiveAgreement[]>([]);
  const [artId, setArtId] = useState('');

  // Getrennt geladen: Ein Fehler bei den Einstellungen soll den Export nicht verdecken.
  useEffect(() => {
    fetchBmdProfile()
      .then(setProfil)
      .catch(() => setProfil(null));
    fetchCollectiveAgreements()
      .then(setArten)
      .catch(() => setArten([]));
  }, []);
  useEffect(() => {
    setZuordnungen(null);
    fetchBmdWageTypes(artId)
      .then(setZuordnungen)
      .catch(() => setZuordnungen([]));
  }, [artId]);

  const erzeugen = async () => {
    if (!vorschau) return;
    if (
      uebergaben.length > 0 &&
      umfang === 'ALL' &&
      !(await bestaetige(
        `Für ${monatName} gibt es schon eine BMD-Datei. Die neue Datei enthält wieder alle fertiggestellten Monate — beim Einlesen in BMD ersetzt sie die frühere. Neu erzeugen?`,
        { ok: 'Datei erzeugen' },
      ))
    )
      return;
    setLaeuft(true);
    setError(null);
    setMeldung(null);
    try {
      const u = await createBmdHandover(monat, umfang);
      setMeldung(`${u.filename} erzeugt: ${u.employeeCount} Mitarbeiter, ${u.lineCount} ${u.lineCount === 1 ? 'Zeile' : 'Zeilen'}.`);
      await ladeDatei(u);
      await ladeMonat();
    } catch (err) {
      setError(apiErrorText(err, 'Die BMD-Datei konnte nicht erzeugt werden.'));
    } finally {
      setLaeuft(false);
    }
  };

  const nichtsZuTun = !vorschau || vorschau.zeilen === 0 || vorschau.zuordnungen === 0;

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">BMD-Export</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Die fertiggestellten Monate als Datei für den Import in BMD — je Mitarbeiter und Lohnart eine Zeile mit der Menge.
          </p>
        </div>
        <div className="flex items-center rounded-md border border-border">
          <button
            type="button"
            className="rounded-l-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setzeMonat(verschiebe(monat, -1))}
            aria-label="Vormonat"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">{monatName}</span>
          <button
            type="button"
            className="rounded-r-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setzeMonat(verschiebe(monat, 1))}
            aria-label="Folgemonat"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {meldung && (
        <div className="rounded-md border border-green-600/30 bg-green-600/10 px-4 py-2 text-sm text-foreground">{meldung}</div>
      )}

      <Abschnitt titel={`Export ${monatName}`}>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="bmd-umfang"
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={umfang === 'ALL'}
                onChange={() => setUmfang('ALL')}
              />
              alle fertiggestellten Monate
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="bmd-umfang"
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={umfang === 'NEW'}
                onChange={() => setUmfang('NEW')}
              />
              nur neue und seit dem letzten Export geänderte
            </label>
          </div>

          {vorschau && (
            <>
              <p className="text-sm text-foreground">
                <span className="font-medium tabular-nums">{vorschau.mitarbeiter}</span> Mitarbeiter ·{' '}
                <span className="font-medium tabular-nums">{vorschau.zeilen}</span> {vorschau.zeilen === 1 ? 'Zeile' : 'Zeilen'} ·{' '}
                <span className="text-muted-foreground">{vorschau.dateiname}</span>
              </p>
              {vorschau.zuordnungen === 0 && (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Es ist noch keine Lohnart zugeordnet — ordne unten mindestens einer Größe eine Lohnart zu.
                </p>
              )}
              {vorschau.warnungen.length > 0 && (
                <ul className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                  {vorschau.warnungen.map((w) => (
                    <li key={w.code} className="flex items-start gap-1.5">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                      <span>
                        {WARNUNG_TEXT[w.code]}
                        {w.namen?.length ? `: ${w.namen.join(', ')}` : '.'}
                        {w.code === 'NO_PERSONNEL_NUMBER' && (
                          <>
                            {' — '}
                            <Link to="/payroll/employees" className="text-primary hover:underline">
                              Personalnummern eintragen
                            </Link>
                          </>
                        )}
                        {w.code === 'NOT_FINAL' && (
                          <>
                            {' — '}
                            <Link to={`/payroll/months?month=${monat}`} className="text-primary hover:underline">
                              Zur Monatsübersicht
                            </Link>
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {vorschau.zeilen > 0 && (
                <div>
                  <p className="mb-1 text-xs text-muted-foreground">
                    Vorschau{vorschau.zeilen > 20 ? ' — die ersten 20 Zeilen' : ''}
                  </p>
                  <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-2 text-xs leading-relaxed">
                    {vorschau.vorschau}
                  </pre>
                </div>
              )}
            </>
          )}

          <Button size="sm" disabled={nichtsZuTun || laeuft} onClick={() => void erzeugen()}>
            <FileOutput className="h-4 w-4" />
            BMD-Datei erzeugen
          </Button>
        </div>
      </Abschnitt>

      <Abschnitt titel="Bisherige Exporte">
        {uebergaben.length === 0 ? (
          <p className="text-sm text-muted-foreground">Für {monatName} wurde noch keine Datei erzeugt.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {uebergaben.map((u, i) => (
              <li key={u.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
                <span className="tabular-nums text-foreground">{zeitpunkt(u.createdAt)}</span>
                <span className="text-muted-foreground">{u.createdBy}</span>
                <span className="tabular-nums text-muted-foreground">
                  {u.employeeCount} Mitarbeiter · {u.lineCount} {u.lineCount === 1 ? 'Zeile' : 'Zeilen'}
                </span>
                {i === 0 && uebergaben.length > 1 && <span className="rounded bg-muted px-1.5 text-xs">zuletzt</span>}
                <span className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
                    onClick={() => void ladeDatei(u).catch(() => setError('Die Datei konnte nicht geladen werden.'))}
                  >
                    <Download size={13} aria-hidden="true" />
                    {u.filename}
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => setMailFuer(u)}
                    title={t('mailExport.menu')}
                    aria-label={t('mailExport.menu')}
                  >
                    <Mail size={14} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>

      <Abschnitt titel="Lohnarten">
        <div className="mb-3 space-y-1.5 text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Was ist eine Lohnart?</span> In BMD hat jede Art von Bezug oder Zeit eine
            eigene Nummer — die Lohnart, etwa für Normalstunden, Überstunden oder Urlaub. Über diese Nummer weiß BMD beim
            Einlesen, wohin ein Wert gehört. Die Nummern legt dein Lohnbüro in BMD fest; du findest sie im Lohnartenverzeichnis
            deines BMD oder bei deiner Steuerberatung.
          </p>
          <p>
            Trag hier je Größe der Monatsauswertung die Nummer ein, unter der sie nach BMD gehen soll. Ohne Lohnart wird die
            Größe nicht übergeben — du musst also nur zuordnen, was BMD von dir bekommen soll.{' '}
            {darfEinstellen ? 'Eine Zeile wird gespeichert, sobald du sie verlässt.' : 'Die Zuordnung ändert, wer den BMD-Export einstellen darf.'}
          </p>
        </div>
        {arten.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <label htmlFor="bmd-art" className="text-muted-foreground">
              Zuordnung für
            </label>
            <select id="bmd-art" className={`${feldKlasse} w-auto`} value={artId} onChange={(e) => setArtId(e.target.value)}>
              <option value="">alle</option>
              {arten.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {artId && (
              <span className="text-xs text-muted-foreground">
                Ein leeres Feld heißt: wie „für alle" (grau angezeigt). Eine eigene Nummer gilt nur für Mitarbeiter mit dieser
                Entlohnungsart; „Aktiv" aus schaltet die Größe für sie ab.
              </span>
            )}
          </div>
        )}
        <ZeilenListe spalten={LOHNART_SPALTEN}>
          {zuordnungen === null && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>}
          {zuordnungen?.map((z) => (
            <ZuordnungZeile
              key={`${artId}:${z.quantity}`}
              zuordnung={z}
              artId={artId}
              gesperrt={!darfEinstellen}
              onGespeichert={() => void ladeMonat()}
            />
          ))}
        </ZeilenListe>
      </Abschnitt>

      {profil && <Format profil={profil} gesperrt={!darfEinstellen} onGespeichert={() => void ladeMonat()} />}

      {/* AP-10 Nachtrag 5: dieselbe Datei per E-Mail. Sie ist schon erzeugt — Format und Ausrichtung gibt es nicht. */}
      <MailExportDialog
        open={mailFuer !== null}
        onClose={() => setMailFuer(null)}
        source="EXPORT_PAYROLL_BMD"
        title={mailFuer?.filename ?? ''}
        defaultSubject={`${t('mailExport.titles.EXPORT_PAYROLL_BMD')} ${monatName}`}
        request={() => bmdFileRequest(mailFuer?.id ?? '')}
        formats={['xlsx']}
        showOrientation={false}
      />
    </div>
  );
}

// ── Lohnarten ───────────────────────────────────────────────────────────────

const LOHNART_SPALTEN: SpaltenDef[] = [
  { key: 'wageType', label: 'Lohnart', spur: '8rem', min: 90, schmal: 'w-28' },
  { key: 'label', label: 'Bezeichnung', spur: 'minmax(12rem,3fr)', min: 180, schmal: 'w-full' },
  { key: 'unit', label: 'Einheit', spur: '8rem', min: 90, schmal: 'w-28' },
  { key: 'nlzCode', label: 'NLZ-Kennz.', spur: '7rem', min: 80, schmal: 'w-24' },
  { key: 'active', label: 'Aktiv', spur: '4rem', min: 48, schmal: 'w-16' },
];

function ZuordnungZeile({
  zuordnung,
  artId,
  gesperrt,
  onGespeichert,
}: {
  zuordnung: BmdZuordnung;
  /** Entlohnungsart, deren Überschreibung die Zeile pflegt; leer = „für alle". */
  artId: string;
  gesperrt: boolean;
  onGespeichert: () => void;
}) {
  const pfad = `/payroll/bmd/wage-types/${encodeURIComponent(zuordnung.quantity)}${artId ? `?agreement=${artId}` : ''}`;
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `payrollBmdWageType:${artId || 'alle'}:${zuordnung.quantity}`,
    bezeichnung: `Lohnart ${zuordnung.label}`,
    daten: zuordnung as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api.put<Datensatz>(pfad, patch, mitStand(stand)).then((r) => {
        onGespeichert();
        return r.data;
      }),
    keepalive: { url: pfad, method: 'PUT' },
  }) as Speicherstelle<unknown>;
  const gruppe = useGruppe(stelle, { label: zuordnung.label });
  const text = { lies: (t: string) => t.trim(), schreib: (w: string) => w ?? '' };
  const lohnart = useFeld<string>(stelle, 'wageType', { gruppe, label: 'Lohnart', gesperrt, ...text });
  const einheit = useFeld<string>(stelle, 'unit', { gruppe, art: 'auswahl', label: 'Einheit', gesperrt });
  const nlz = useFeld<string>(stelle, 'nlzCode', { gruppe, label: 'NLZ-Kennzeichen', gesperrt, ...text });
  const aktiv = useFeld<boolean>(stelle, 'active', { gruppe, art: 'schalter', label: 'Aktiv', gesperrt });
  const fehlzeit = zuordnung.quantity.startsWith('FEHLZEIT:');

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={zuordnung.quantity}>
      <Spalte spalte="wageType">
        <input
          aria-label={`Lohnart ${zuordnung.label}`}
          maxLength={20}
          className={`${zelle} tabular-nums`}
          placeholder={zuordnung.inherited?.wageType ?? ''}
          data-start
          {...lohnart.props}
        />
      </Spalte>
      <Spalte spalte="label">
        <span className="block py-1 text-sm text-foreground" title={zuordnung.hinweis ?? undefined}>
          {zuordnung.label}
          {zuordnung.hinweis && <span className="block text-xs text-muted-foreground">{zuordnung.hinweis}</span>}
        </span>
      </Spalte>
      <Spalte spalte="unit">
        {zuordnung.einheiten.length > 1 ? (
          <select aria-label="Einheit" className={zelle} {...einheit.props}>
            {zuordnung.einheiten.map((e) => (
              <option key={e} value={e}>
                {EINHEIT_TEXT[e]}
              </option>
            ))}
          </select>
        ) : (
          <span className="block py-1 text-sm text-muted-foreground">{EINHEIT_TEXT[zuordnung.einheiten[0]]}</span>
        )}
      </Spalte>
      <Spalte spalte="nlzCode">
        {fehlzeit ? (
          <input aria-label="NLZ-Kennzeichen" maxLength={20} className={zelle} {...nlz.props} />
        ) : (
          <span />
        )}
      </Spalte>
      <Spalte spalte="active">
        <span className="flex items-center justify-center gap-1">
          <input type="checkbox" aria-label="Aktiv" className="h-4 w-4 accent-[hsl(var(--primary))]" {...aktiv.schalter} />
          <FeldStatusZeichen status={aktiv} />
        </span>
      </Spalte>
    </ZeilenBlock>
  );
}

// ── Format der Datei ────────────────────────────────────────────────────────

function Format({ profil, gesperrt, onGespeichert }: { profil: BmdProfil; gesperrt: boolean; onGespeichert: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'payrollBmdProfile',
    bezeichnung: 'Format der BMD-Datei',
    daten: profil as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api.put<Datensatz>('/payroll/bmd/profile', patch, mitStand(stand)).then((r) => {
        onGespeichert();
        return r.data;
      }),
    keepalive: { url: '/payroll/bmd/profile', method: 'PUT' },
  }) as Speicherstelle<unknown>;
  const text = { lies: (t: string) => t.trim(), schreib: (w: string) => w ?? '' };
  const klient = useFeld<string>(stelle, 'klientennummer', { label: 'Klientennummer', gesperrt, ...text });
  const dateiname = useFeld<string>(stelle, 'dateiname', { label: 'Dateiname', gesperrt, pflicht: true, ...text });
  const trennzeichen = useFeld<string>(stelle, 'trennzeichen', { art: 'auswahl', label: 'Trennzeichen', gesperrt });
  const dezimal = useFeld<string>(stelle, 'dezimal', { art: 'auswahl', label: 'Dezimalzeichen', gesperrt });
  const zeichensatz = useFeld<string>(stelle, 'zeichensatz', { art: 'auswahl', label: 'Zeichensatz', gesperrt });
  const kopfzeile = useFeld<boolean>(stelle, 'kopfzeile', { art: 'schalter', label: 'Kopfzeile', gesperrt });
  const spalten = ((useStellenDaten(stelle).spalten as BmdSpalte[] | undefined) ?? profil.spalten).map((s) => ({ ...s }));

  // Die Spalten gelten nur zusammen: Die Liste geht als Ganzes (AP-36 8.3 a).
  const setzeSpalten = (aendere: (liste: BmdSpalte[]) => BmdSpalte[]) =>
    void stelle.sende({
      kennung: 'spalten',
      felder: ['spalten'],
      labels: { spalten: 'Spalten' },
      operation: (aktuell) => ({ spalten: aendere(((aktuell.spalten as BmdSpalte[]) ?? []).map((s) => ({ ...s }))) }),
    });
  const tausche = (i: number, j: number) =>
    setzeSpalten((l) => {
      if (j < 0 || j >= l.length) return l;
      [l[i], l[j]] = [l[j], l[i]];
      return l;
    });

  return (
    <Abschnitt titel="Format der Datei">
      <p className="mb-3 text-xs text-muted-foreground">
        Die Satzbeschreibung des BMD-Imports („Abrechnung Import") liegt nicht vor. Stell Spalten und Zeichen so ein, wie
        sie dein BMD erwartet — die Vorschau oben zeigt das Ergebnis sofort.
      </p>
      <div className="grid gap-x-8 gap-y-3 lg:grid-cols-2">
        <div className="space-y-3">
          <FeldZeile label="Klientennummer" status={klient}>
            <input className={feldKlasse} maxLength={30} {...klient.props} />
          </FeldZeile>
          <FeldZeile label="Dateiname" status={dateiname} hilfe="{JJJJ}, {MM} und {KLIENT} werden ersetzt.">
            <input className={feldKlasse} maxLength={80} {...dateiname.props} />
          </FeldZeile>
          <FeldZeile label="Trennzeichen" status={trennzeichen}>
            <select className={feldKlasse} {...trennzeichen.props}>
              <option value=";">Strichpunkt ( ; )</option>
              <option value=",">Komma ( , )</option>
              <option value="TAB">Tabulator</option>
              <option value="|">Senkrechter Strich ( | )</option>
            </select>
          </FeldZeile>
          <FeldZeile label="Dezimalzeichen" status={dezimal}>
            <select className={feldKlasse} {...dezimal.props}>
              <option value=",">Komma (12,50)</option>
              <option value=".">Punkt (12.50)</option>
            </select>
          </FeldZeile>
          <FeldZeile label="Zeichensatz" status={zeichensatz}>
            <select className={feldKlasse} {...zeichensatz.props}>
              <option value="windows-1252">Windows-1252 (ANSI)</option>
              <option value="utf-8">UTF-8</option>
            </select>
          </FeldZeile>
          <SchalterZeile feld={kopfzeile} label="Kopfzeile mit den Spaltennamen" />
        </div>

        <div>
          <p className="mb-1 text-sm text-foreground">Spalten, in dieser Reihenfolge</p>
          <ol className="space-y-1">
            {spalten.map((s, i) => (
              <li key={`${s.art}-${i}`} className="flex items-center gap-1.5 text-sm">
                <span className="w-5 text-right text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{SPALTEN_TEXT[s.art]}</span>
                {s.art === 'FEST' && (
                  <input
                    aria-label="Fester Wert"
                    className={`${feldKlasse} w-28`}
                    placeholder="Wert"
                    disabled={gesperrt}
                    defaultValue={s.wert ?? ''}
                    key={s.wert ?? ''}
                    onBlur={(e) => {
                      const wert = e.target.value;
                      if (wert !== (s.wert ?? '')) setzeSpalten((l) => l.map((x, k) => (k === i ? { ...x, wert } : x)));
                    }}
                  />
                )}
                {!gesperrt && (
                  <>
                    <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30" disabled={i === 0} onClick={() => tausche(i, i - 1)} aria-label="Nach oben">
                      <ArrowUp size={13} />
                    </button>
                    <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30" disabled={i === spalten.length - 1} onClick={() => tausche(i, i + 1)} aria-label="Nach unten">
                      <ArrowDown size={13} />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
                      disabled={spalten.length === 1}
                      onClick={() => setzeSpalten((l) => l.filter((_, k) => k !== i))}
                      aria-label="Spalte entfernen"
                    >
                      <X size={13} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ol>
          {!gesperrt && (
            <label className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Plus size={14} aria-hidden="true" />
              <select
                aria-label="Spalte hinzufügen"
                className={`${feldKlasse} w-auto`}
                value=""
                onChange={(e) => {
                  const art = e.target.value as BmdSpaltenArt;
                  if (art) setzeSpalten((l) => [...l, art === 'FEST' ? { art, wert: '' } : { art }]);
                }}
              >
                <option value="">Spalte hinzufügen …</option>
                {(Object.keys(SPALTEN_TEXT) as BmdSpaltenArt[]).map((a) => (
                  <option key={a} value={a}>
                    {SPALTEN_TEXT[a]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
    </Abschnitt>
  );
}
